import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { ArrowRight, HeartPulse, Stethoscope, Building2 } from 'lucide-react';

export function FilmmakerDashboard() {
  return (
    <div className="min-h-screen bg-[#212323] text-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gray-900 py-24">
        <div className="absolute inset-0 bg-gradient-to-r from-[#00539B]/90 to-[#212323]/90" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Welcome to <span className="text-[#F8B500]">Northwell Studios</span>
            </h1>
            <h2 className="text-2xl md:text-3xl text-gray-300 mb-8">
              Discover a Studio Where Your Vision Meets Our Mission
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              At Northwell Studios, we believe in the power of storytelling to transform and humanize healthcare. 
              As a for-profit entity nestled within a vast health system, our mission extends beyond traditional 
              film production to spotlight stories that resonate deeply and often go untold. With nearly 100,000 
              employees and 19 million square feet of clinical and retail space at our disposal, we offer a unique 
              platform for filmmakers to bring their creative visions to life.
            </p>
          </div>
        </div>
      </div>

      {/* What We Stand For */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-4">What We Stand For</h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Our studio is built on the foundation of sparking meaningful social dialogue and strengthening 
            the Northwell brand. We are passionate about healthcare, mental health, gun violence prevention, 
            fertility, and cutting-edge research and innovation. These themes guide our selection of projects 
            as we aim to create premium content that achieves wide-reaching impact and distribution.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <div className="bg-gray-800 rounded-lg p-6">
            <HeartPulse className="h-12 w-12 text-[#F8B500] mb-4" />
            <h3 className="text-xl font-semibold mb-3">Healthcare Focus</h3>
            <p className="text-gray-300">
              We prioritize stories that humanize healthcare and bring often taboo medical narratives to life.
            </p>
          </div>

          <div className="bg-gray-800 rounded-lg p-6">
            <Building2 className="h-12 w-12 text-[#F8B500] mb-4" />
            <h3 className="text-xl font-semibold mb-3">Unique Resources</h3>
            <p className="text-gray-300">
              Access to 19 million square feet of clinical and retail space, providing unparalleled filming opportunities.
            </p>
          </div>

          <div className="bg-gray-800 rounded-lg p-6">
            <Stethoscope className="h-12 w-12 text-[#F8B500] mb-4" />
            <h3 className="text-xl font-semibold mb-3">Industry Expertise</h3>
            <p className="text-gray-300">
              Backed by a network of nearly 100,000 healthcare professionals and industry leaders.
            </p>
          </div>
        </div>

        {/* Support and Collaboration */}
        <div className="bg-gray-800 rounded-lg p-8 mb-16">
          <h2 className="text-3xl font-bold mb-6">Support and Collaboration</h2>
          <p className="text-gray-300 mb-8 text-lg">
            Northwell Studios provides comprehensive support spanning development funding, production resources, 
            post-production tools, and impact campaign resources. We seek to work with filmmakers who not only 
            possess professional experience, creative vision, and technical skills but who also share our 
            collaborative spirit and cultural ethos.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-xl font-semibold mb-3">Resources We Offer</h3>
              <div className="flex flex-wrap gap-2">
                {[
                  'Development Funding',
                  'Production Resources',
                  'Post-production Tools',
                  'Impact Campaign Resources',
                  'Marketing Support',
                  'Distribution Strategy'
                ].map((type) => (
                  <span
                    key={type}
                    className="px-3 py-1 bg-[#F8B500]/10 text-[#F8B500] rounded-full text-sm"
                  >
                    {type}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-3">Content We Accept</h3>
              <div className="flex flex-wrap gap-2">
                {[
                  'Documentary Feature',
                  'Documentary Series',
                  'Narrative Feature',
                  'Narrative Series'
                ].map((format) => (
                  <span
                    key={format}
                    className="px-3 py-1 bg-[#F8B500]/10 text-[#F8B500] rounded-full text-sm"
                  >
                    {format}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Our Expectations */}
        <div className="bg-gray-800 rounded-lg p-8 mb-16">
          <h2 className="text-3xl font-bold mb-6">Our Expectations</h2>
          <div className="space-y-4 text-gray-300 text-lg">
            <p>
              While we are open to bold topics and new perspectives, maintaining high production value is paramount. 
              We avoid overtly political content to maintain a bipartisan approach.
            </p>
            <p>
              Filmmakers working with us must navigate strict legal guidelines and our detailed review processes, 
              especially when filming in sensitive environments like hospitals.
            </p>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center bg-[#F8B500] rounded-lg p-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Ready to Start Your Journey?</h2>
          <p className="text-xl text-gray-800 mb-8 max-w-2xl mx-auto">
            Are you ready to begin the submission process and potentially join a studio that values impactful 
            storytelling in healthcare? If you share our commitment to creating narratives that matter and have 
            a project that aligns with our goals, we would love to hear from you.
          </p>
          <Link to="/filmmaker-application">
            <Button size="lg" className="bg-gray-900 text-white hover:bg-gray-800">
              Yes, I'm Ready to Begin
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}